import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AutoclavesPage() {
  const [autoclaves, setAutoclaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAutoclave, setEditingAutoclave] = useState(null);
  const [formData, setFormData] = useState({
    identificacao: '',
    marca: '',
    modelo: '',
    numero_serie: '',
    tipo: 'vapor',
    data_calibracao: '',
    requer_bowie_dick: true,
  });

  useEffect(() => {
    fetchAutoclaves();
  }, []);

  const fetchAutoclaves = async () => {
    try {
      const response = await api.get('/autoclaves');
      setAutoclaves(response.data);
    } catch (error) {
      toast.error('Erro ao carregar autoclaves');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAutoclave) {
        await api.put(`/autoclaves/${editingAutoclave.id}`, formData);
        toast.success('Autoclave atualizada com sucesso!');
      } else {
        await api.post('/autoclaves', formData);
        toast.success('Autoclave criada com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchAutoclaves();
    } catch (error) {
      toast.error('Erro ao salvar autoclave');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta autoclave?')) return;
    try {
      await api.delete(`/autoclaves/${id}`);
      toast.success('Autoclave excluída com sucesso!');
      fetchAutoclaves();
    } catch (error) {
      toast.error('Erro ao excluir autoclave');
    }
  };

  const handleEdit = (autoclave) => {
    setEditingAutoclave(autoclave);
    setFormData({
      identificacao: autoclave.identificacao,
      marca: autoclave.marca,
      modelo: autoclave.modelo,
      numero_serie: autoclave.numero_serie,
      tipo: autoclave.tipo,
      data_calibracao: autoclave.data_calibracao,
      requer_bowie_dick: autoclave.requer_bowie_dick !== false,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAutoclave(null);
    setFormData({
      identificacao: '',
      marca: '',
      modelo: '',
      numero_serie: '',
      tipo: 'vapor',
      data_calibracao: '',
      requer_bowie_dick: true,
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div data-testid="autoclaves-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Autoclaves
          </h1>
          <p className="text-lg text-slate-600 mt-2">Gerenciar equipamentos de esterilização</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-autoclave"
              onClick={() => resetForm()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Autoclave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingAutoclave ? 'Editar Autoclave' : 'Nova Autoclave'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="identificacao">Identificação</Label>
                  <Input
                    data-testid="input-identificacao"
                    id="identificacao"
                    value={formData.identificacao}
                    onChange={(e) => setFormData({ ...formData, identificacao: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="tipo">Tipo</Label>
                  <select
                    data-testid="select-tipo"
                    id="tipo"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="vapor">Vapor</option>
                    <option value="eto">Óxido de Etileno</option>
                    <option value="peroxido">Peróxido de Hidrogênio</option>
                    <option value="formaldeido">Formaldeído</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="marca">Marca</Label>
                  <Input
                    data-testid="input-marca"
                    id="marca"
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="modelo">Modelo</Label>
                  <Input
                    data-testid="input-modelo"
                    id="modelo"
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="numero_serie">Número de Série</Label>
                  <Input
                    data-testid="input-numero-serie"
                    id="numero_serie"
                    value={formData.numero_serie}
                    onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="data_calibracao">Data de Calibração</Label>
                  <Input
                    data-testid="input-calibracao"
                    id="data_calibracao"
                    type="date"
                    value={formData.data_calibracao}
                    onChange={(e) => setFormData({ ...formData, data_calibracao: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                <input
                  data-testid="checkbox-bowie-dick"
                  type="checkbox"
                  id="requer_bowie_dick"
                  checked={formData.requer_bowie_dick}
                  onChange={(e) => setFormData({ ...formData, requer_bowie_dick: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <div>
                  <Label htmlFor="requer_bowie_dick" className="font-medium cursor-pointer">Requer Teste Bowie-Dick</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Se marcado, exigirá teste B&D aprovado no primeiro ciclo do dia</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="btn-submit" type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingAutoclave ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Identificação</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Marca/Modelo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nº Série</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">B&D</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {autoclaves.map((autoclave) => (
              <tr key={autoclave.id} data-testid={`autoclave-row-${autoclave.id}`} className="hover:bg-slate-50">
                <td className="px-6 py-4 text-sm font-medium text-slate-900">{autoclave.identificacao}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{autoclave.marca} {autoclave.modelo}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{autoclave.tipo}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{autoclave.numero_serie}</td>
                <td className="px-6 py-4 text-sm">
                  {autoclave.requer_bowie_dick !== false ? (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">Obrigatório</span>
                  ) : (
                    <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded text-xs font-medium">Dispensado</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={autoclave.status} />
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2">
                    <Button
                      data-testid={`btn-edit-${autoclave.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(autoclave)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-delete-${autoclave.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(autoclave.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {autoclaves.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Nenhuma autoclave cadastrada
          </div>
        )}
      </div>
    </div>
  );
}